/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { addDirtyState } from 'src/core/utils/decorators/addDirtyState';
import { getEntityRoleService } from 'src/luis/core/services/entities/entityRoleService';
import { getEntityHandlerGroupUtils } from 'src/luis/projects/entities/utils/entityHelpers';
import { LuisId } from 'src/luis/types/Resource';
import { EntityRoleStore } from 'src/luis/types/stores/entities/EntityRoleStore';

export type EntityRoleHandlerGroup = ReturnType<typeof createEntityRoleHandlerGroup>;

/**
 * Represents a handler group for entity role operations.
 *
 * @param dependencies The handler group dependencies.
 */
export const createEntityRoleHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    const { getEntityStore } = getEntityHandlerGroupUtils(rootStore);

    const logger = getLogger();

    /**
     * Creates a new entity role and adds it to the given entity.
     *
     * @param entityId The id of the entity to add the role to.
     * @param roleName The name of the new entity role to be added.
     */
    const loadEntityRoles = async ({ entityId, context }: HandlerParams<{ entityId: LuisId }>) => {
        const { appId, versionId } = context;
        const entityStore = getEntityStore({ appId, versionId, entityId });
        const entityRoleService = getEntityRoleService({ context: { ...context, entityType: entityStore.typeId } });
        const logTimedActivity = logger.createTimedActivityLogger<readonly EntityRoleStore[]>('LoadEntityRoles', () => ({ entityId }));
        const entityRoles = await logTimedActivity(entityRoleService.getEntityRoles({ entityId }));

        entityRoles.forEach(entityRole => entityStore.entityRoleStoreHouse.add(entityRole));
    };

    /**
     * Loads all entity roles that belong to the given entity.
     *
     * @param entityId The id of the entity to load the entity roles for.
     */
    const addEntityRole = async ({ entityId, roleName, context, tracker }: HandlerParams<{ entityId: LuisId; roleName: string }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const entityStore = getEntityStore({ appId, versionId, entityId });
            const entityRoleService = getEntityRoleService({ context: { ...context, entityType: entityStore.typeId } });
            const logTimedActivity = logger.createTimedActivityLogger<EntityRoleStore>('AddEntityRole', () => ({
                entityId,
                entityName: entityStore.name,
                roleName
            }));

            const entityRole = await logTimedActivity(entityRoleService.addEntityRole({ entityId, roleName }));

            tracker.setContext(entityRole.id);

            entityStore.entityRoleStoreHouse.add(entityRole, 'back');
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Deletes an entity roles from a given entity.
     *
     * @param entityId The id of the entity the entity role belongs to.
     * @param roleIds The names of the role to be deleted.
     */
    const deleteEntityRoles = async ({ entityId, roleIds, context }: HandlerParams<{ entityId: LuisId; roleIds: LuisId[] }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const entityStore = getEntityStore({ appId, versionId, entityId });
            const entityRoleService = getEntityRoleService({ context: { ...context, entityType: entityStore.typeId } });
            const logTimedActivity = logger.createTimedActivityLogger<void[]>('DeleteEntityRoles', () => ({
                entityId,
                entityName: entityStore.name
            }));

            const servicePromises = roleIds.map(roleId => entityRoleService.deleteEntityRole({ entityId, roleId }));

            await logTimedActivity(Promise.all(servicePromises));

            entityStore.entityRoleStoreHouse.batchRemoveByIds(roleIds);
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Renames an entity role within a given entity.
     *
     * @param entityId The id of the entity that the entity role belongs to.
     * @param roleId The id of the entity role to be renamed.
     * @param newName The new name for the entity role.
     */
    const renameEntityRole = async ({
        entityId,
        roleId,
        newName,
        context
    }: HandlerParams<{ entityId: LuisId; roleId: LuisId; newName: string }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const entityStore = getEntityStore({ appId, versionId, entityId });
            const entityRoleService = getEntityRoleService({ context: { ...context, entityType: entityStore.typeId } });
            const logTimedActivity = logger.createTimedActivityLogger('RenameEntityRole', () => ({
                entityId,
                entityName: entityStore.name,
                newName
            }));

            await logTimedActivity(entityRoleService.renameEntityRole({ entityId, roleId, newName }));

            entityStore.entityRoleStoreHouse.findById(roleId).rename(newName);
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    return {
        loadEntityRoles,
        addEntityRole,
        deleteEntityRoles,
        renameEntityRole
    };
};
